import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface RuntimeApiConfig {
  identity: string;
  backend: string;
}

const RUNTIME_CONFIG_URL = '/runtime-config.json';

@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private apiConfig: RuntimeApiConfig | null = null;

  get identity(): string {
    return this.requireConfig().identity;
  }

  get backend(): string {
    return this.requireConfig().backend;
  }

  async load(): Promise<void> {
    try {
      this.apiConfig = await fetchRuntimeConfig();
    } catch (error) {
      // `npm start` (ng serve) has no container entrypoint to render
      // /runtime-config.json; fall back to the URLs generated from .env.
      const fallback = environmentApiConfig();

      if (fallback) {
        this.apiConfig = fallback;
        return;
      }

      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  private requireConfig(): RuntimeApiConfig {
    if (!this.apiConfig) {
      throw new Error('Runtime configuration has not been loaded');
    }

    return this.apiConfig;
  }
}

async function fetchRuntimeConfig(): Promise<RuntimeApiConfig> {
  let response: Response;

  try {
    response = await fetch(RUNTIME_CONFIG_URL, { cache: 'no-store' });
  } catch (error) {
    throw new Error(
      `Unable to load runtime configuration from ${RUNTIME_CONFIG_URL}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }

  if (!response.ok) {
    throw new Error(
      `Unable to load runtime configuration from ${RUNTIME_CONFIG_URL}: HTTP ${response.status}`,
    );
  }

  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    throw new Error(`Runtime configuration at ${RUNTIME_CONFIG_URL} is not valid JSON`);
  }

  const api = (payload as { api?: { identity?: unknown; backend?: unknown } } | null)?.api;

  return {
    identity: requireHttpUrl('api.identity', api?.identity),
    backend: requireHttpUrl('api.backend', api?.backend),
  };
}

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return false;
  }

  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function requireHttpUrl(key: string, value: unknown): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Runtime configuration is missing a value for "${key}"`);
  }

  if (!isHttpUrl(value)) {
    throw new Error(`Runtime configuration "${key}" must be an http(s) URL: ${value}`);
  }

  return value;
}

function environmentApiConfig(): RuntimeApiConfig | null {
  const { apiBaseUrl, opsApiBaseUrl } = environment;

  if (!isHttpUrl(apiBaseUrl) || !isHttpUrl(opsApiBaseUrl)) {
    return null;
  }

  return { identity: apiBaseUrl, backend: opsApiBaseUrl };
}
